import { cancelPendingRequest, client } from '@/api/client'
import type { Estimate, EstimateResult } from '@/types/estimation'

/**
 * Fetches the estimation averages through the proxy route on the corporate website, like the
 * calculation. One request the first time the modal opens; every click after that combines the
 * tables locally, so the numbers move without a wait while they stay owned by capi (#6324).
 */
export async function requestEstimation(): Promise<EstimateResult> {
  const response = await client.get<EstimateResult>('/api/net-metering/estimation/', {
    signal: cancelPendingRequest('net-metering.estimation'),
  })

  return response.data
}

export type Estimator = {
  panelLimits: { min: number; max: number }
  estimate(persons: number, panels: number): Estimate
}

/**
 * National averages do not change within a visit, so the first fetch serves every later open of
 * the modal: no skeleton the second time. A failed fetch is not cached; the next open just tries
 * again.
 */
let cached: Estimator | null = null

export async function loadEstimator(): Promise<Estimator> {
  cached ??= buildEstimator(await requestEstimation())

  return cached
}

/** Split from the fetch so the combining is testable on its own. */
export function buildEstimator(averages: EstimateResult): Estimator {
  return {
    panelLimits: averages.limits.solar_panels,
    estimate: (persons: number, panels: number): Estimate => ({
      electricity: averages.electricity_usage_by_persons[persons],
      generation: Math.round(averages.generation_per_panel * panels),
      gas: averages.gas_usage_by_persons[persons],
    }),
  }
}
